import React, { useEffect, useMemo, useState } from "react";
import {
  X,
  Save,
  Plus,
  Trash2,
  Upload,
  FileDown,
  RefreshCcw,
} from "lucide-react";
import Papa from "papaparse";
import { supabase } from "../../Services/supabaseClient";
import { useCompany } from "../../context/CompanyContext";
import "../Accounting/FixedAssets/fixedAssets.css";

function todayISO() {
  const d = new Date();
  return d.toISOString().slice(0, 10);
}

function safeStr(x) {
  return String(x ?? "").trim();
}
function safeLower(x) {
  return safeStr(x).toLowerCase();
}
function toNum(x) {
  const n = Number(
    String(x ?? "")
      .replace(/,/g, "")
      .trim()
  );
  return Number.isFinite(n) ? n : 0;
}

function downloadMovementTemplate() {
  // NOTE: warehouse is NOT in the CSV (selected in modal)
  // item can be identified by sku (recommended)
  const headers = [
    "sku", // required
    "quantity", // required
    "unit_cost", // required for IN; required for ADJUSTMENT direction=IN
    "direction", // required only for ADJUSTMENT (IN|OUT)
    "batch_no", // optional
    "expiry_date", // optional (YYYY-MM-DD)
  ];

  const rows = [
    ["SKU-001", "10", "35000", "", "", ""],
    ["SKU-002", "2", "1200", "", "", ""],
    ["SKU-003", "3", "", "OUT", "", ""], // e.g. for ADJUSTMENT OUT you can omit unit_cost
  ];

  const csv =
    headers.join(",") +
    "\n" +
    rows
      .map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(","))
      .join("\n");

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "stock_movement_template.csv";
  a.click();
  URL.revokeObjectURL(url);
}

export default function PostMovementModal({ onClose, onPosted }) {
  const { companyId } = useCompany();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const [warehouses, setWarehouses] = useState([]);
  const [items, setItems] = useState([]);

  const [movementType, setMovementType] = useState("IN");
  const [movementDate, setMovementDate] = useState(todayISO());
  const [warehouseId, setWarehouseId] = useState("");
  const [warehouseToId, setWarehouseToId] = useState("");
  const [notes, setNotes] = useState("");

  // Manual lines (existing feature)
  const [lines, setLines] = useState([
    { item_id: "", quantity: "1", unit_cost: "0", direction: "IN" },
  ]);

  // ✅ Bulk CSV upload state (new feature)
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkRows, setBulkRows] = useState([]); // normalized CSV rows
  const [bulkResults, setBulkResults] = useState([]); // results/validation feedback

  useEffect(() => {
    (async () => {
      if (!companyId) return;

      const { data: wh, error: whErr } = await supabase
        .from("warehouses")
        .select("id,name,is_default")
        .eq("company_id", companyId)
        .order("name", { ascending: true });

      if (!whErr) {
        setWarehouses(wh || []);
        const def = (wh || []).find((w) => w.is_default) || (wh || [])[0];
        if (def?.id) setWarehouseId(def.id);
      }

      const { data: it, error: itErr } = await supabase
        .from("inventory_items")
        .select(
          "id,sku,name,item_type,unit,inventory_account_id,cogs_account_id,adjustment_account_id"
        )
        .eq("company_id", companyId)
        .eq("is_active", true)
        .order("name", { ascending: true });

      if (!itErr) setItems(it || []);
    })();
  }, [companyId]);

  const isTransfer = movementType === "TRANSFER";

  // ---- manual helpers (unchanged) ----
  function setLine(idx, patch) {
    setLines((p) => p.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  }
  function addLine() {
    setLines((p) => [
      ...p,
      { item_id: "", quantity: "1", unit_cost: "0", direction: "OUT" },
    ]);
  }
  function removeLine(idx) {
    setLines((p) => p.filter((_, i) => i !== idx));
  }

  // ---- Edge function caller (unchanged) ----
  async function callFn(payload) {
    const { data: sessionData } = await supabase.auth.getSession();
    const token = sessionData?.session?.access_token;
    if (!token) throw new Error("Not authenticated.");

    const bad = (payload.lines || []).filter((l) => !l.item_id);
    if (bad.length) {
      setErr("Some lines are missing item_id. Please reselect items.");
      console.error("Lines missing item_id:", bad);
      return;
    }

    const fnUrl = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/inventory-post-movement`;
    const res = await fetch(fnUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    });

    const raw = await res.text();
    let json;
    try {
      json = raw ? JSON.parse(raw) : {};
    } catch {
      json = { error: raw || "Unknown error" };
    }

    if (!res.ok) {
      console.error("inventory-post-movement failed:", {
        status: res.status,
        statusText: res.statusText,
        payload,
        json,
        raw,
      });
      throw new Error(json?.error || `Request failed (${res.status})`);
    }

    return json;
  }

  // =========================
  // ✅ BULK CSV IMPLEMENTATION
  // =========================

  const itemIdBySku = useMemo(() => {
    const m = new Map();
    (items || []).forEach((it) => m.set(safeLower(it.sku), it.id));
    return m;
  }, [items]);

  const validateBulkRows = useMemo(() => {
    const issues = [];
    if (!bulkRows.length) return { issues, hasIssues: false };

    bulkRows.forEach((r, idx) => {
      const rowNo = idx + 2; // header row = 1
      if (!r.sku) issues.push({ rowNo, sku: r.sku, msg: "SKU is required" });
      if (!r.quantity || r.quantity <= 0)
        issues.push({ rowNo, sku: r.sku, msg: "Quantity must be > 0" });

      const itemId = itemIdBySku.get(safeLower(r.sku));
      if (!itemId)
        issues.push({
          rowNo,
          sku: r.sku,
          msg: "SKU not found in inventory items",
        });

      if (movementType === "IN") {
        if (r.unit_cost == null || r.unit_cost < 0)
          issues.push({
            rowNo,
            sku: r.sku,
            msg: "Stock IN requires unit_cost >= 0",
          });
      }

      if (movementType === "ADJUSTMENT") {
        if (!r.direction || !["IN", "OUT"].includes(r.direction))
          issues.push({
            rowNo,
            sku: r.sku,
            msg: "Adjustment requires direction IN/OUT",
          });

        if (r.direction === "IN" && (r.unit_cost == null || r.unit_cost < 0))
          issues.push({
            rowNo,
            sku: r.sku,
            msg: "Adjustment IN requires unit_cost >= 0",
          });
      }

      // For OUT, unit_cost is optional (your function uses avg/FIFO)
      // For TRANSFER, same as OUT costing-wise
    });

    return { issues, hasIssues: issues.length > 0 };
  }, [bulkRows, movementType, itemIdBySku]);

  const handleBulkFile = async (file) => {
    setErr("");
    setBulkResults([]);
    setBulkRows([]);

    if (!file) return;

    const ext = safeLower(file.name).split(".").pop();
    if (ext !== "csv") {
      setErr("Please upload a CSV file.");
      return;
    }

    setBusy(true);
    try {
      const text = await file.text();
      const parsed = Papa.parse(text, {
        header: true,
        skipEmptyLines: true,
        transformHeader: (h) => safeLower(h),
      });

      if (parsed.errors?.length) {
        console.error(parsed.errors);
        setErr(parsed.errors[0]?.message || "CSV parse error.");
        setBusy(false);
        return;
      }

      const rows = (parsed.data || []).map((r) => {
        const sku = safeStr(r.sku);
        const quantity = toNum(r.quantity);
        const unit_cost_raw = safeStr(r.unit_cost);
        const unit_cost = unit_cost_raw === "" ? null : toNum(unit_cost_raw);
        const direction = safeStr(r.direction).toUpperCase() || "";
        const batch_no = safeStr(r.batch_no) || null;
        const expiry_date = safeStr(r.expiry_date) || null;

        return { sku, quantity, unit_cost, direction, batch_no, expiry_date };
      });

      setBulkRows(rows);
      setBulkResults([
        { status: "ok", message: `Loaded ${rows.length} rows from CSV.` },
      ]);
    } catch (e) {
      setErr(e?.message || "Failed to read file.");
    } finally {
      setBusy(false);
    }
  };

  const bulkToLines = () => {
    // Convert CSV rows into function lines payload (item_id-based)
    const clean = bulkRows
      .map((r) => {
        const item_id = itemIdBySku.get(safeLower(r.sku)) || "";
        return {
          item_id,
          quantity: Number(r.quantity || 0),
          unit_cost: r.unit_cost === "" ? null : r.unit_cost,
          direction: r.direction || "IN", // for adjustment
          batch_no: r.batch_no || null,
          expiry_date: r.expiry_date || null,
        };
      })
      .filter((l) => l.item_id && l.quantity > 0);

    return clean;
  };

  async function postBulk() {
    setErr("");
    setBulkResults([]);

    if (!companyId) {
      setErr("Company ID missing. Reload dashboard or re-login.");
      return;
    }

    if (!warehouseId) return setErr("Select a warehouse.");
    if (isTransfer && !warehouseToId)
      return setErr("Select destination warehouse for transfers.");
    if (isTransfer && warehouseToId === warehouseId)
      return setErr("Destination warehouse must be different.");

    if (!bulkRows.length) return setErr("Upload a CSV file first.");

    if (validateBulkRows.hasIssues) {
      setBulkResults(
        validateBulkRows.issues.slice(0, 25).map((x) => ({
          status: "error",
          message: `Row ${x.rowNo} ${x.sku ? `(${x.sku})` : ""}: ${x.msg}`,
        }))
      );
      setErr("Fix CSV validation issues and try again.");
      return;
    }

    const cleanLines = bulkToLines();
    if (!cleanLines.length) return setErr("No valid lines found in CSV.");

    setBusy(true);
    try {
      const json = await callFn({
        company_id: companyId,
        movement_type: movementType,
        movement_date: movementDate,
        warehouse_id: warehouseId,
        warehouse_to_id: isTransfer ? warehouseToId : null,
        notes: notes?.trim() || null,
        reference_type: "bulk_upload",
        reference_id: null,
        post_to_journal: true,
        lines: cleanLines,
      });

      setBulkResults([
        { status: "ok", message: "Bulk movement posted successfully." },
      ]);
      onPosted?.(json);
    } catch (e) {
      setErr(e.message || "Error");
      setBulkResults([{ status: "error", message: e.message || "Error" }]);
    } finally {
      setBusy(false);
    }
  }

  // =========================
  // Manual post (existing)
  // =========================
  async function post() {
    setErr("");

    if (!companyId) {
      setErr("Company ID missing. Reload dashboard or re-login.");
      return;
    }

    if (!warehouseId) return setErr("Select a warehouse.");
    if (isTransfer && !warehouseToId)
      return setErr("Select destination warehouse for transfers.");
    if (isTransfer && warehouseToId === warehouseId)
      return setErr("Destination warehouse must be different.");

    const cleanLines = lines
      .map((l) => ({
        item_id: l.item_id,
        quantity: Number(l.quantity || 0),
        unit_cost: l.unit_cost === "" ? null : Number(l.unit_cost || 0),
        direction: l.direction, // used for ADJUSTMENT
      }))
      .filter((l) => l.item_id && l.quantity > 0);

    if (cleanLines.length === 0) return setErr("Add at least one valid line.");

    if (movementType === "IN") {
      const bad = cleanLines.find(
        (l) => l.unit_cost == null || l.unit_cost < 0
      );
      if (bad) return setErr("Stock IN requires unit cost on each line.");
    }

    if (movementType === "ADJUSTMENT") {
      const bad = cleanLines.find(
        (l) => !l.direction || !["IN", "OUT"].includes(l.direction)
      );
      if (bad) return setErr("Adjustment requires direction IN/OUT per line.");

      const needCost = cleanLines.find(
        (l) => l.direction === "IN" && (l.unit_cost == null || l.unit_cost < 0)
      );
      if (needCost) return setErr("Adjustment IN requires unit cost.");
    }

    setBusy(true);
    try {
      const json = await callFn({
        company_id: companyId,
        movement_type: movementType,
        movement_date: movementDate,
        warehouse_id: warehouseId,
        warehouse_to_id: isTransfer ? warehouseToId : null,
        notes: notes?.trim() || null,
        reference_type: "manual",
        reference_id: null,
        post_to_journal: true,
        lines: cleanLines,
      });

      onPosted?.(json);
    } catch (e) {
      setErr(e.message || "Error");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.97)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 14,
        zIndex: 9999,
      }}
      onClick={onClose}
    >
      <div
        className="fa-panel"
        style={{ width: "min(980px, 100%)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{ display: "flex", justifyContent: "space-between", gap: 10 }}
        >
          <div>
            <div style={{ fontSize: 18, fontWeight: 900 }}>
              Post Stock Movement
            </div>
            <div style={{ opacity: 0.8, fontSize: 13, marginTop: 4 }}>
              Posts inventory subledger + journals + low stock notifications.
            </div>
          </div>
          <button
            className="fa-icon-btn"
            onClick={onClose}
            title="Close"
            disabled={busy}
          >
            <X size={16} />
          </button>
        </div>

        {/* Movement header */}
        <div className="fa-toolbar" style={{ marginTop: 12, flexWrap: "wrap" }}>
          <select
            className="fa-select"
            value={movementType}
            onChange={(e) => setMovementType(e.target.value)}
            disabled={busy}
          >
            <option value="IN">Stock IN</option>
            <option value="OUT">Stock OUT</option>
            <option value="ADJUSTMENT">Adjustment</option>
            <option value="TRANSFER">Transfer</option>
          </select>

          <input
            className="fa-input"
            type="date"
            value={movementDate}
            onChange={(e) => setMovementDate(e.target.value)}
            disabled={busy}
          />

          <select
            className="fa-select"
            value={warehouseId}
            onChange={(e) => setWarehouseId(e.target.value)}
            disabled={busy}
          >
            <option value="">Select warehouse…</option>
            {warehouses.map((w) => (
              <option key={w.id} value={w.id}>
                {w.name}
                {w.is_default ? " (Default)" : ""}
              </option>
            ))}
          </select>

          {isTransfer && (
            <select
              className="fa-select"
              value={warehouseToId}
              onChange={(e) => setWarehouseToId(e.target.value)}
              disabled={busy}
            >
              <option value="">To warehouse…</option>
              {warehouses.map((w) => (
                <option key={w.id} value={w.id}>
                  {w.name}
                </option>
              ))}
            </select>
          )}

          {/* ✅ toggle modes (new, but does not remove manual feature) */}
          <button
            className="fa-btn"
            onClick={() => {
              setErr("");
              setBulkResults([]);
              setBulkRows([]);
              setBulkMode((v) => !v);
            }}
            disabled={busy}
            style={{ marginLeft: "auto" }}
            title="Switch between manual lines and bulk upload"
          >
            {bulkMode ? "Manual Entry" : "Bulk Upload"}
          </button>
        </div>

        {/* ✅ Bulk Upload Section */}
        {bulkMode && (
          <div className="fa-card" style={{ marginTop: 12 }}>
            <div
              style={{
                display: "flex",
                gap: 10,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <div style={{ fontWeight: 900 }}>Bulk Upload (CSV)</div>

              <button
                className="fa-btn"
                onClick={downloadMovementTemplate}
                disabled={busy}
              >
                <FileDown size={16} /> Template
              </button>

              <label
                className="fa-btn"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  cursor: "pointer",
                }}
              >
                <Upload size={16} /> Upload CSV
                <input
                  type="file"
                  accept=".csv,text/csv"
                  style={{ display: "none" }}
                  onChange={(e) => handleBulkFile(e.target.files?.[0])}
                  disabled={busy}
                />
              </label>

              <button
                className="fa-btn"
                onClick={() => {
                  setBulkRows([]);
                  setBulkResults([]);
                  setErr("");
                }}
                disabled={busy}
              >
                <RefreshCcw size={16} /> Clear
              </button>

              <div style={{ marginLeft: "auto", opacity: 0.85, fontSize: 13 }}>
                Rows loaded: <b>{bulkRows.length}</b>
              </div>
            </div>

            {/* Preview */}
            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: 12, opacity: 0.8, marginBottom: 6 }}>
                Preview (first 15 rows)
              </div>
              <div className="fa-panel" style={{ padding: 0 }}>
                <table className="fa-table">
                  <thead>
                    <tr>
                      <th>SKU</th>
                      <th>Qty</th>
                      <th>Unit Cost</th>
                      <th>Direction</th>
                      <th>Batch</th>
                      <th>Expiry</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bulkRows.slice(0, 15).map((r, idx) => (
                      <tr key={idx}>
                        <td>{r.sku || "—"}</td>
                        <td>{r.quantity}</td>
                        <td>{r.unit_cost == null ? "—" : r.unit_cost}</td>
                        <td>{r.direction || "—"}</td>
                        <td>{r.batch_no || "—"}</td>
                        <td>{r.expiry_date || "—"}</td>
                      </tr>
                    ))}
                    {bulkRows.length === 0 && (
                      <tr>
                        <td colSpan={6} style={{ opacity: 0.75 }}>
                          No CSV rows loaded.
                        </td>
                      </tr>
                    )}
                    {bulkRows.length > 15 && (
                      <tr>
                        <td colSpan={6} style={{ opacity: 0.75 }}>
                          Showing 15 of {bulkRows.length} rows.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Validation quick view */}
              {validateBulkRows.issues.length > 0 && (
                <div style={{ marginTop: 10, fontSize: 13, color: "#ffb4b4" }}>
                  {validateBulkRows.issues.slice(0, 6).map((x, i) => (
                    <div key={i}>
                      Row {x.rowNo} {x.sku ? `(${x.sku})` : ""}: {x.msg}
                    </div>
                  ))}
                  {validateBulkRows.issues.length > 6 && (
                    <div>…and {validateBulkRows.issues.length - 6} more</div>
                  )}
                </div>
              )}

              {/* Results */}
              {bulkResults.length > 0 && (
                <div style={{ marginTop: 10, fontSize: 13 }}>
                  {bulkResults.map((r, i) => (
                    <div
                      key={i}
                      style={{
                        color: r.status === "error" ? "#ffb4b4" : "#d7ffd7",
                      }}
                    >
                      {r.message}
                    </div>
                  ))}
                </div>
              )}

              <div
                className="fa-actions"
                style={{ marginTop: 12, justifyContent: "flex-end" }}
              >
                <button className="fa-btn" onClick={onClose} disabled={busy}>
                  Cancel
                </button>
                <button
                  className="fa-btn fa-btn-primary"
                  onClick={postBulk}
                  disabled={busy}
                >
                  <Save size={16} /> {busy ? "Posting…" : "Post Bulk Movement"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Manual Section (existing) */}
        {!bulkMode && (
          <>
            <div className="fa-card" style={{ marginTop: 12 }}>
              <div style={{ fontWeight: 900, marginBottom: 10 }}>Lines</div>

              {lines.map((l, idx) => (
                <div
                  key={idx}
                  className="fa-toolbar"
                  style={{ marginBottom: 8 }}
                >
                  <select
                    className="fa-select"
                    value={l.item_id}
                    onChange={(e) => setLine(idx, { item_id: e.target.value })}
                    disabled={busy}
                  >
                    <option value="">Select item…</option>
                    {items.map((it) => {
                      const needsMap =
                        it.item_type === "stock" && !it.inventory_account_id;
                      const label = `${it.sku} - ${it.name} (${it.unit})${
                        needsMap ? " — ⚠️ Missing Inventory Mapping" : ""
                      }`;

                      return (
                        <option key={it.id} value={it.id} disabled={needsMap}>
                          {label}
                        </option>
                      );
                    })}
                  </select>

                  <input
                    className="fa-input"
                    type="number"
                    placeholder="Qty"
                    value={l.quantity}
                    onChange={(e) => setLine(idx, { quantity: e.target.value })}
                    disabled={busy}
                  />

                  {movementType === "ADJUSTMENT" && (
                    <select
                      className="fa-select"
                      value={l.direction}
                      onChange={(e) =>
                        setLine(idx, { direction: e.target.value })
                      }
                      disabled={busy}
                    >
                      <option value="OUT">OUT (shrinkage)</option>
                      <option value="IN">IN (found stock)</option>
                    </select>
                  )}

                  {(movementType === "IN" ||
                    (movementType === "ADJUSTMENT" &&
                      l.direction === "IN")) && (
                    <input
                      className="fa-input"
                      type="number"
                      placeholder="Unit cost"
                      value={l.unit_cost}
                      onChange={(e) =>
                        setLine(idx, { unit_cost: e.target.value })
                      }
                      disabled={busy}
                    />
                  )}

                  <button
                    className="fa-btn"
                    onClick={() => removeLine(idx)}
                    disabled={busy || lines.length === 1}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}

              <button className="fa-btn" onClick={addLine} disabled={busy}>
                <Plus size={16} /> Add line
              </button>
            </div>

            <div style={{ marginTop: 10 }}>
              <div style={{ fontSize: 12, opacity: 0.8, marginBottom: 6 }}>
                Notes (optional)
              </div>
              <textarea
                className="fa-textarea"
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                disabled={busy}
              />
            </div>

            {err && (
              <div style={{ marginTop: 10, color: "#ffb4b4", fontSize: 13 }}>
                {err}
              </div>
            )}

            <div
              className="fa-actions"
              style={{ marginTop: 12, justifyContent: "flex-end" }}
            >
              <button className="fa-btn" onClick={onClose} disabled={busy}>
                Cancel
              </button>
              <button
                className="fa-btn fa-btn-primary"
                onClick={post}
                disabled={busy}
              >
                <Save size={16} /> {busy ? "Posting…" : "Post Movement"}
              </button>
            </div>
          </>
        )}

        {/* show errors below bulk too */}
        {bulkMode && err && (
          <div style={{ marginTop: 10, color: "#ffb4b4", fontSize: 13 }}>
            {err}
          </div>
        )}
      </div>
    </div>
  );
}
